import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ImagesIcon } from 'lucide-react';
import { TabPanel, Tabs } from '../ui/Tabs';
import { EmptyState } from '../ui/EmptyState';
import { ScaledDesign } from './ScaledDesign';
import { useAdmin } from '../AdminContext';
import { campaignOrder, templateByKey } from './marketingOptions';
import { btnSecondary, inputCls } from '../ui/styles';
import { designPlan, isPlannable, type DesignPlan } from './designPlanning';
import { relativeDay } from '../../core/format/dates';
import type { Channel } from '../../core/types/marketing';
const channels: ('Alles' | Channel)[] = ['Alles', 'Instagram', 'Print', 'E-mail'];

/** Campagnes die niet in de vaste volgorde staan, komen achteraan. */
const rank = (c: string) => {
  const i = campaignOrder.indexOf(c);
  return i === -1 ? campaignOrder.length : i;
};

/** Waar een Instagram-ontwerp in de planning staat, als één rustige regel onder de kaart. */
function PlanLine({
  plan
}: {
  plan: DesignPlan;
}) {
  const day = (date: string) => relativeDay(date).replace(/^(Vandaag|Morgen|Gisteren)$/, w => w.toLowerCase());
  const text = plan.kind === 'gepland' ? `Gepland: ${day(plan.date)}` : plan.kind === 'geplaatst' ? `Geplaatst: ${day(plan.date)}` : plan.kind === 'zonder-datum' ? 'In de planning, nog zonder datum' : 'Nog niet ingepland';
  return <p className={`mt-1 flex items-center gap-1.5 truncate text-[12px] ${plan.kind === 'gepland' ? 'text-ink' : 'text-muted'}`} data-id="element-3020" data-mp="2ep8apgbrdj9p">
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${plan.kind === 'gepland' ? 'bg-sage' : plan.kind === 'niet-gepland' ? 'bg-clay' : 'bg-line'}`} aria-hidden="true" data-id="element-3021" data-mp="2jobdsv86hff1" />
      <span className="truncate" data-id="element-3022" data-mp="2sopypth8csi0">{text}</span>
    </p>;
}

/** Alle ontwerpen, gefilterd op kanaal en campagne. Tabblad van /be/ontwerpen (Designs.tsx geeft de kop). */
export function Marketing() {
  const {
    marketing
  } = useAdmin();
  const [channel, setChannel] = useState<(typeof channels)[number]>('Alles');
  const [campaign, setCampaign] = useState('');
  const campaigns = useMemo(() => {
    const all = Array.from(new Set(marketing.designs.map(d => d.campaign).filter(Boolean)));
    return all.sort((a, b) => rank(a) - rank(b));
  }, [marketing.designs]);
  const rows = marketing.designs.filter(d => (channel === 'Alles' || templateByKey(d.template).channel === channel) && (!campaign || d.campaign === campaign));
  return <div data-id="element-3023" data-mp="1l8t4dhqdw2t0">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-3024" data-mp="1l8j9pgue97gz">
        <Tabs id="marketing-kanaal" label="Kanaal" variant="pill" value={channel} onChange={setChannel} tabs={channels.map(c => ({
        key: c,
        label: c,
        count: c === 'Alles' ? undefined : marketing.designs.filter(d => templateByKey(d.template).channel === c).length
      }))} data-id="element-3025" data-mp="1g9ybfmypcxv6" />
        <select value={campaign} onChange={e => setCampaign(e.target.value)} aria-label="Campagne" className={`${inputCls} sm:w-56`} data-id="element-3026" data-mp="xj73mpud5fiw">
          <option value="" data-id="element-3027" data-mp="3dzwrj8qntbwh">Alle campagnes</option>
          {campaigns.map(c => <option key={c} data-id="element-3028" data-mp="307ylj6s8vw4j">{c}</option>)}
        </select>
      </div>

      <TabPanel id="marketing-kanaal" value={channel} data-id="element-3029" data-mp="3065fntpghfxl">
      {rows.length === 0 ? <EmptyState icon={ImagesIcon} title="Geen ontwerpen in deze selectie" text="Kies een ander kanaal of een andere campagne, of begin met een nieuw ontwerp." action={<button type="button" onClick={() => {
        setChannel('Alles');
        setCampaign('');
      }} className={btnSecondary} data-id="element-3031" data-mp="qh7psc218wa">
              Toon alles
            </button>} data-id="element-3030" data-mp="349tn8ce38biu" /> : <ul className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 xl:grid-cols-4" data-id="element-3032" data-mp="27ldyujmps87b">
          {rows.map(d => {
          const t = templateByKey(d.template);
          return <li key={d.id} data-id="element-3033" data-mp="2rtc7anqifdep">
                <Link to={`/be/ontwerpen/${d.id}`} className="group block" data-id="element-3034" data-mp="2n1kmyuxy63x0">
                  {/*
                    Het verkleinde ontwerp is een plaatje van Sarah's eigen artwork (haar kleuren, haar foto):
                    voor schermlezers en contrastcontrole één afbeelding, niet losse tekst van een paar pixels.
                    Naam en formaat staan eronder als echte tekst.
                   */}
                  <div role="img" aria-label={`Voorbeeld van ${d.name || 'naamloos ontwerp'}`} className="aspect-[4/5] rounded-lg bg-white/70 p-3 ring-1 ring-line transition-colors duration-150 ease-soft group-hover:bg-white sm:p-5" data-id="element-3035" data-mp="35rahri1pmpox">
                    <ScaledDesign design={d} fill data-id="element-3036" data-mp="1qbblb41ggi4i" />
                  </div>
                  <p className="mt-3 truncate text-[14px] text-ink" data-id="element-3037" data-mp="1lc8g9jlh6bwj">{d.name || 'Naamloos ontwerp'}</p>
                  <p className="truncate text-[12px] text-muted" data-id="element-3038" data-mp="u6dhsvba9as">
                    {t.name} · {t.sizeLabel}
                  </p>
                  {isPlannable(d) && <PlanLine plan={designPlan(d.id, marketing.posts)} data-id="element-3039" data-mp="5t978h5uct3g" />}
                </Link>
              </li>;
        })}
        </ul>}
      </TabPanel>
    </div>;
}